/**
 * Internationalization Configuration
 */

export const locales = ['en', 'nl', 'de', 'sv'] as const;
export type Locale = typeof locales[number];

export const defaultLocale: Locale = 'en';

export const localeNames: Record<Locale, string> = {
  en: 'English',
  nl: 'Nederlands',
  de: 'Deutsch',
  sv: 'Svenska',
};

export const localeFlags: Record<Locale, string> = {
  en: '🇬🇧',
  nl: '🇳🇱',
  de: '🇩🇪',
  sv: '🇸🇪',
};

/**
 * Hreflang values per locale.
 * Swedish uses `sv-SE` to target the Swedish market explicitly.
 */
export const localeHreflang: Record<Locale, string> = {
  en: 'en',
  nl: 'nl',
  de: 'de',
  sv: 'sv-SE',
};

/**
 * Open Graph locale identifiers per locale.
 */
export const localeOgLocale: Record<Locale, string> = {
  en: 'en_GB',
  nl: 'nl_NL',
  de: 'de_DE',
  sv: 'sv_SE',
};

/**
 * Full BCP-47 locale codes for schema.org `inLanguage` and the `<html lang>`
 * attribute. Use deze i.p.v. inline `locale === 'nl' ? 'nl-NL' : ...` ternaries.
 */
export const localeBcp47: Record<Locale, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  de: 'de-DE',
  sv: 'sv-SE',
};

/**
 * Get locale-specific path.
 * English (default) doesn't get a prefix; other languages get `/{locale}` prepended.
 *
 * Idempotent: if `path` already starts with `/{locale}` or equals `/{locale}`,
 * it's returned as-is. This lets callers safely pre-prefix data (so React Server
 * Component hydration payloads contain the final URL) without rendering layers
 * double-prefixing on re-application.
 */
export function getLocalePath(locale: Locale, path: string = ''): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (locale === defaultLocale) {
    return cleanPath;
  }
  if (cleanPath === `/${locale}` || cleanPath.startsWith(`/${locale}/`)) {
    return cleanPath;
  }
  return `/${locale}${cleanPath === '/' ? '' : cleanPath}`;
}

/**
 * Check if locale is valid
 */
export function isValidLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}

/**
 * Pre-prefix all `href` fields in an array of items with the correct locale path.
 *
 * Why: client components (Header/Footer) receive these arrays as props and
 * serialise them into the RSC flight payload. If hrefs are stored without a
 * locale prefix (e.g. `/cookiepolicy`) the hydration JSON contains the raw
 * path even when the rendered `<a href>` is correct. Crawlers (Semrush, GSC)
 * pick those raw paths up as internal links and report 301 redirect chains
 * (`/cookiepolicy` → `/sv/cookiepolicy`).
 *
 * Wrapping the data at the boundary makes the hydration payload contain the
 * final URL while leaving downstream `getLocalePath` calls idempotent.
 */
export function prefixHrefs<T extends { href: string }>(
  locale: Locale,
  items: readonly T[],
): T[] {
  return items.map((item) => ({ ...item, href: getLocalePath(locale, item.href) }));
}
